"use client";

import ProductCardListBySearchTabClient from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/ProductCardListBySearchTabClient";
import type {
  Category as SearchTabCategory,
  ProductCardListBySearchTabProps,
} from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/types";
import mergeCategories from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/utils/mergeCategories";
import useFeatureFlag from "@/lib/feature-flags/hooks/useFeatureFlag";
import ProductCardTabsAndPillsByUUIDClient from "./ProductCardTabsAndPillsByUUIDClient";
import type { ProductCardTabsAndPillsByUUIDProps } from "./types";
import { mapUuidTabsToSearchTabCategories } from "./utils/mapUuidTabsToSearchTabCategories";
import useHomepageTrendingCourseUpdate from "@/monarch/rules/homepageTrendingCourseUpdate/useHomepageTrendingCourseUpdate";
import useTrendingProductsApi from "../ProductCardListBySearchTab/useTrendingProductsApi";
import TrendingProductCardListSkeleton from "../ProductCardListBySearchTab/TrendingProductCardListSkeleton";

const CARDS_PER_ROW = 4;
const TRENDING_PRODUCTS_HITS_PER_PAGE = 8;

function toSearchTabProps(
  props: ProductCardTabsAndPillsByUUIDProps,
  categoriesForSearchTab?: SearchTabCategory[],
): ProductCardListBySearchTabProps {
  const { title, className, categories, location, cardFormatSubtype } = props;
  return {
    title,
    className,
    categories:
      categoriesForSearchTab ??
      mapUuidTabsToSearchTabCategories(categories ?? []),
    location,
    cardFormatSubtype,
  };
}

/**
 * Resolves `NEXT_PUBLIC_FEATURE_HOMEPAGE_TRENDING_COURSE_UPDATE` on the client (env + API),
 * then renders the search-tab trending UI when `test`, otherwise the UUID pills/accordion UI.
 */
export default function ProductCardTabsAndPillsByUUIDFeatureBranch(
  props: ProductCardTabsAndPillsByUUIDProps,
) {
  const { flagValue, isLoading } = useFeatureFlag(
    "NEXT_PUBLIC_FEATURE_HOMEPAGE_TRENDING_COURSE_UPDATE",
    "legacy",
  );

  const trendingTabs = useHomepageTrendingCourseUpdate({
    enabled: flagValue === "test",
  });

  const probeTransformApi =
    flagValue === "test" &&
    !trendingTabs.isLoading &&
    trendingTabs.data.enabled;

  const maxExpandedRows =
    trendingTabs.data.expandableFooter.maxExpandedRows ?? 2;
  const cmsCategoriesAsSearchTab = mapUuidTabsToSearchTabCategories(
    props.categories ?? [],
  );
  const mergedCategories = mergeCategories(
    cmsCategoriesAsSearchTab,
    trendingTabs.data.categories,
  );

  const requestedHitsPerPage = maxExpandedRows * CARDS_PER_ROW;
  const hitsPerPage = Math.min(
    requestedHitsPerPage,
    TRENDING_PRODUCTS_HITS_PER_PAGE,
  );

  const { data: trendingProductsData, isLoading: trendingProductsLoading } =
    useTrendingProductsApi({
      categories: mergedCategories,
      hitsPerPage,
      fallbackCategories: cmsCategoriesAsSearchTab,
      enabled: probeTransformApi,
    });

  if (isLoading || trendingTabs.isLoading) {
    return <TrendingProductCardListSkeleton cardCount={4} />;
  }

  if (flagValue === "test") {
    if (trendingTabs.data.enabled) {
      const { data } = trendingTabs;
      const categoriesForSearchTab =
        trendingProductsData ?? cmsCategoriesAsSearchTab;

      return (
        <ProductCardListBySearchTabClient
          {...toSearchTabProps(props, categoriesForSearchTab)}
          categoryKeyOrder={data.categoryKeyOrder}
          prioritizeCoursesFirst={data.prioritizeCoursesFirst}
          showTrendingFlameIcon={data.showTrendingFlameIcon}
          expandableFooter={data.expandableFooter}
          isLoading={
            trendingProductsLoading || trendingProductsData === undefined
          }
        />
      );
    }
  }
  return <ProductCardTabsAndPillsByUUIDClient {...props} />;
}
